// CustomHtmlSection.js — Sección de HTML libre
//
// Animación al hacer scroll: cualquier elemento del HTML con una de estas
// clases aparece deslizándose cuando entra en pantalla (una sola vez):
//   anim-izquierda · anim-derecha · anim-arriba · anim-abajo
// (también valen las del plugin de WordPress `sae-from-left|right|top|bottom`,
// para pegar HTML de Divi tal cual). `data-retraso="200"` lo retrasa en ms.
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const DESPLAZAMIENTOS = {
  'anim-izquierda': 'translate3d(-40px, 0, 0)',
  'sae-from-left': 'translate3d(-40px, 0, 0)',
  'anim-derecha': 'translate3d(40px, 0, 0)',
  'sae-from-right': 'translate3d(40px, 0, 0)',
  'anim-arriba': 'translate3d(0, -40px, 0)',
  'sae-from-top': 'translate3d(0, -40px, 0)',
  'anim-abajo': 'translate3d(0, 40px, 0)',
  'sae-from-bottom': 'translate3d(0, 40px, 0)',
};
const SELECTOR_ANIM = Object.keys(DESPLAZAMIENTOS).map(c => `.${c}`).join(', ');

// El estado inicial (oculto) solo se aplica cuando el observador ya está
// vigilando (`data-revelar`): sin JavaScript, o si algo falla, el contenido se
// ve normal en vez de quedar invisible.
const revelarCss = Object.entries(DESPLAZAMIENTOS).map(([clase, t]) => `
  &[data-revelar] .${clase}:not(.anim-visible) { opacity: 0; transform: ${t}; }
`).join('');

const Wrapper = styled.section`
  &[data-revelar] :is(${SELECTOR_ANIM}) {
    transition: transform 700ms ease, opacity 700ms ease;
    will-change: transform, opacity;
  }
  ${revelarCss}
  ${p => p.$css || ''}
`;

const CustomHtmlSection = ({ config }) => {
  const ref = useRef(null);

  useEffect(() => {
    const raiz = ref.current;
    if (!raiz) return undefined;
    const nodos = raiz.querySelectorAll(SELECTOR_ANIM);
    if (!nodos.length) return undefined;
    const sinMovimiento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (sinMovimiento || typeof IntersectionObserver === 'undefined') return undefined;

    const obs = new IntersectionObserver((entradas) => {
      entradas.forEach((en) => {
        if (!en.isIntersecting) return;
        const retraso = parseInt(en.target.getAttribute('data-retraso'), 10);
        if (retraso > 0) en.target.style.transitionDelay = `${retraso}ms`;
        en.target.classList.add('anim-visible');
        obs.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    nodos.forEach(n => obs.observe(n));
    raiz.setAttribute('data-revelar', '');
    return () => obs.disconnect();
  }, [config.html]);

  if (!config.html) return null;

  return (
    <Wrapper ref={ref} $css={config.css || ''}>
      <div dangerouslySetInnerHTML={{ __html: config.html }} />
    </Wrapper>
  );
};

export default CustomHtmlSection;
